/**
 * Copy Shapes Tool Schema
 * 
 * Copy shapes from template components or slides to the current slide.
 * Note: Does NOT support metadata - use liveBuildArea.copy for that.
 */

import { z } from "zod";

// Import shared schemas
import { 
    PositionSchema, 
    TargetAreaSchema, 
    TextFrameSchema, 
    TextContentSchema 
} from "../../../tools/liveBuildArea/liveBuildArea.schema";

// ═══════════════════════════════════════════════════════════════════════════
// COPY SHAPES SPECIFIC SCHEMAS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Individual shape to copy with optional overrides
 */
export const ShapeToCopySchema = z.object({
    shapeId: z.number().int()
        .describe("Shape ID to copy from source (REQUIRED)"),
    
    targetPosition: PositionSchema.optional()
        .describe("Optional position override for this shape"),
    
    textFrame: TextFrameSchema.optional()
        .describe("Optional text frame property overrides"),
    
    textContent: TextContentSchema.optional()
        .describe("Optional text replacement (uses paragraphs[])"),
});

/**
 * Grid layout cell grouping for row/column inserts
 */
export const GridCellSchema = z.object({
    row: z.number().int().min(1)
        .describe("1-based row index for this cell"),
    column: z.number().int().min(1)
        .describe("1-based column index for this cell"),
    shapeIds: z.array(z.number().int())
        .min(1)
        .describe("Shape IDs that visually make up this grid item (e.g., background + text + icon). This is a layout cell, not a table cell."),
});

/**
 * Grid layout definition for insert operations
 */
export const GridLayoutSchema = z.object({
    rows: z.number().int().min(1)
        .describe("Total rows in the existing grid"),
    columns: z.number().int().min(1)
        .describe("Total columns in the existing grid"),
    cells: z.array(GridCellSchema)
        .min(1)
        .describe("Grid cell definitions (row/column + shapeIds for each grid item)"),
    gridBounds: TargetAreaSchema.optional()
        .describe("Optional bounds for the full grid (defaults to shape bounds)"),
});

/**
 * Grid row/column insertion configuration
 */
export const GridInsertSchema = z.object({
    axis: z.enum(["row", "column"])
        .describe("Insert rows or columns"),
    insertIndex: z.number().int().min(1)
        .describe("1-based row/column index to insert at"),
    position: z.enum(["before", "after"]).optional().default("after")
        .describe("Insert before or after the index"),
    count: z.number().int().min(1).optional().default(1)
        .describe("Number of rows/columns to insert"),
    templateIndex: z.number().int().min(1).optional()
        .describe("Row/column index to copy as a template (defaults to insertIndex)"),
    sizeMode: z.enum(["grow", "fit"]).optional().default("fit")
        .describe("fit keeps the grid within original bounds. grow increases grid size and can cause overlaps if space is tight, but works well when there is ample empty space."),
    preserveAspectRatio: z.boolean().optional().default(true)
        .describe("When fit scaling, preserve shape aspect ratios (default true)"),
    scaleMode: z.enum(["uniform", "fitWidth", "fitHeight"])
        .describe("Scaling mode for fit: 'uniform' keeps aspect ratio (default). 'fitWidth'/'fitHeight' compress only one axis (non-uniform)."),
});

/**
 * Copy source specification
 */
export const CopySourceSchema = z.object({
    componentId: z.string().optional()
        .describe("Template component ID (use this OR slideId)"),
    
    slideId: z.number().int().optional()
        .describe("Slide ID within component or current presentation"),
    
    shapes: z.array(ShapeToCopySchema)
        .min(1)
        .optional()
        .describe("Array of shapes to copy with optional overrides"),
    
    targetArea: TargetAreaSchema.extend({
        scaleMode: z.enum(["uniform", "fitWidth", "fitHeight"])
            .describe("Scaling mode for targetArea: 'uniform' keeps aspect ratio (default). 'fitWidth'/'fitHeight' compress only one axis (non-uniform)."),
    }).optional()
        .describe("Optional group scaling - scale all shapes to fit this area"),

    gridLayout: GridLayoutSchema.optional()
        .describe("Grid layout info required for row/column insertion"),

    gridInsert: GridInsertSchema.optional()
        .describe("Insert rows/columns using a grid layout"),
})
    .refine(
        data => (data.shapes && data.shapes.length > 0) || data.gridInsert,
        { message: "Provide shapes or gridInsert configuration" }
    )
    .refine(
        data => !data.gridInsert || data.gridLayout,
        { message: "gridInsert requires gridLayout" }
    );

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COPY SHAPES PARAMS SCHEMA
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Copy Shapes parameters schema
 */
export const CopyShapesParamsSchema = z.object({
    slideId: z.number()
        .int()
        .min(1)
        .describe("Target slide index (1-based) to copy shapes to"),
    
    copySource: CopySourceSchema
        .describe("Source specification for shapes to copy"),
});

export type CopyShapesParams = z.infer<typeof CopyShapesParamsSchema>;
export type ShapeToCopy = z.infer<typeof ShapeToCopySchema>;
export type CopySource = z.infer<typeof CopySourceSchema>;

